import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AvatarPicker } from "@/components/avatar-picker";
import { useAuth, ROLE_HOME, type Role } from "@/lib/auth";
import type { AvatarKey } from "@/lib/avatars";
import { completeOnboarding } from "@/lib/onboarding.functions";
import { updateMyCenter } from "@/lib/center.functions";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/onboarding")({
  head: () => ({
    meta: [
      { title: "Thiết lập tài khoản — Ezyclass" },
      { name: "description", content: "Hoàn tất thiết lập trung tâm trên Ezyclass: thông tin cơ bản, môn học và vai trò người dùng để bắt đầu quản lý lớp học và học viên." },
      { property: "og:title", content: "Thiết lập tài khoản — Ezyclass" },
      { property: "og:description", content: "Hoàn tất thiết lập trung tâm trên Ezyclass trước khi bắt đầu quản lý lớp học." },
      { property: "og:url", content: "https://ezyclass.com/onboarding" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://ezyclass.com/onboarding" }],
  }),
  component: OnboardingPage,
});


const SUBJECTS = ["Toán", "Văn", "Anh", "Lý", "Hóa", "Sinh", "Sử", "Địa", "Tin học", "Mỹ thuật", "Âm nhạc", "GDCD"];

const CONFETTI_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

function Confetti({ trigger }: { trigger: boolean }) {
  const particles = React.useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        x: (Math.random() - 0.5) * 400,
        y: -100 - Math.random() * 200,
        rotate: Math.random() * 720 - 360,
      })),
    [],
  );
  if (!trigger) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-50 grid place-items-center">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute size-1.5 rounded-sm"
          style={{ background: p.color, width: 6, height: 6 }}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
          animate={{ x: p.x, y: 400, opacity: 0, rotate: p.rotate }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
      ))}
    </div>
  );
}

function ProgressBar({ step }: { step: 1 | 2 }) {
  return (
    <div className="flex items-center gap-3 mb-8">
      <div className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500 font-medium">
        Thiết lập tài khoản
      </div>
      <div className="flex-1 h-1 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
        <motion.div
          className="h-full bg-blue-600 rounded-full"
          initial={false}
          animate={{ width: step === 1 ? "50%" : "100%" }}
          transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
        />
      </div>
      <div className="text-xs text-slate-400 dark:text-slate-500 font-medium">Bước {step}/2</div>
    </div>
  );
}

function SubjectChips({
  value,
  onChange,
  max,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  max?: number;
}) {
  const atMax = max !== undefined && value.length >= max;
  return (
    <div className="flex flex-wrap gap-2">
      {SUBJECTS.map((s) => {
        const selected = value.includes(s);
        const disabled = !selected && atMax;
        return (
          <button
            key={s}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (selected) onChange(value.filter((v) => v !== s));
              else onChange([...value, s]);
            }}
            className={cn(
              "px-3 py-1.5 rounded-full text-sm border transition-all active:scale-[0.97]",
              selected
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:border-slate-700",
              disabled && "opacity-40 cursor-not-allowed",
            )}
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}

function OnboardingPage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const updateCenterFn = useServerFn(updateMyCenter);

  const [step, setStep] = React.useState<1 | 2>(1);
  const [displayName, setDisplayName] = React.useState("");
  const [avatarKey, setAvatarKey] = React.useState<AvatarKey | null>("fox");
  const [customAvatarFile, setCustomAvatarFile] = React.useState<File | null>(null);
  const [customAvatarPreview, setCustomAvatarPreview] = React.useState<string | null>(null);
  const [phone, setPhone] = React.useState("");
  const [dateOfBirth, setDateOfBirth] = React.useState("");
  // Admin
  const [centerName, setCenterName] = React.useState("");
  const [centerAddress, setCenterAddress] = React.useState("");
  // Teacher
  const [classroomName, setClassroomName] = React.useState("");
  const [teachingSubjects, setTeachingSubjects] = React.useState<string[]>([]);
  // Student
  const [favSubjects, setFavSubjects] = React.useState<string[]>([]);
  const [schoolName, setSchoolName] = React.useState("");

  const [submitting, setSubmitting] = React.useState(false);
  const [confetti, setConfetti] = React.useState(false);

  React.useEffect(() => {
    if (user && !displayName) {
      setDisplayName(user.displayName || (user.email ? user.email.split("@")[0] : ""));
      if (user.avatarKey) setAvatarKey(user.avatarKey as AvatarKey);
    }
  }, [user, displayName]);

  React.useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    if (user.onboarded) {
      window.location.assign(ROLE_HOME[user.role]);
    }
  }, [user, loading, navigate]);

  // Prefill center info for admin (created during signup)
  React.useEffect(() => {
    if (!user?.centerId || user.role !== "admin") return;
    supabase.from("centers").select("name, address").eq("id", user.centerId).maybeSingle()
      .then(({ data }) => {
        const c = data as { name?: string; address?: string | null } | null;
        if (c?.name && !centerName) setCenterName(c.name);
        if (c?.address && !centerAddress) setCenterAddress(c.address);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.centerId, user?.role]);

  const handleCustomFile = (f: File) => {
    setCustomAvatarFile(f);
    setCustomAvatarPreview(URL.createObjectURL(f));
  };

  const role: Role = user?.role ?? "student";

  const placeholderName =
    role === "admin" ? "VD: Nguyễn Văn Bình" :
    role === "teacher" ? "VD: Thầy Minh / Cô Hương" :
    "VD: Nguyễn Văn An";

  const canNext = displayName.trim().length > 0 && (avatarKey || customAvatarFile);

  const canSubmit =
    role === "admin" ? centerName.trim().length > 0 :
    role === "teacher" ? classroomName.trim().length > 0 :
    true;

  const handleSubmit = async () => {
    if (!user) return;
    if (!canSubmit) {
      toast.error("Vui lòng điền các trường bắt buộc");
      return;
    }
    setSubmitting(true);
    try {
      let avatarUrl: string | undefined;
      if (customAvatarFile) {
        const ext = customAvatarFile.name.split(".").pop() || "png";
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("avatars")
          .upload(path, customAvatarFile, { upsert: true, contentType: customAvatarFile.type });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
        avatarUrl = pub.publicUrl;
      }

      const extraInfo: Record<string, unknown> =
        role === "admin"
          ? { centerName: centerName.trim(), centerAddress: centerAddress.trim() }
          : role === "teacher"
          ? { classroomName: classroomName.trim(), teachingSubjects }
          : { subjects: favSubjects, schoolName: schoolName.trim() };

      await completeOnboarding({
        data: {
          displayName: displayName.trim(),
          avatarKey: customAvatarFile ? undefined : avatarKey ?? undefined,
          avatarUrl,
          phone: phone.trim() || undefined,
          dateOfBirth: dateOfBirth || undefined,
          extraInfo,
        },
      });

      if (role === "admin" && user.centerId) {
        try {
          await updateCenterFn({
            data: { name: centerName.trim(), address: centerAddress.trim() || null },
          });
        } catch (e) { console.error("updateMyCenter failed", e); }
      }

      setConfetti(true);
      toast.success("Chào mừng đến Ezyclass!");
      await refresh();
      setTimeout(() => {
        window.location.assign(ROLE_HOME[role]);
      }, 900);
    } catch (err: unknown) {
      console.error("Onboarding error:", err);
      const message = err instanceof Error ? err.message : "Không lưu được";
      if (message.includes("row-level security") || message.includes("policy")) {
        toast.error("Lỗi phân quyền. Vui lòng đăng xuất và đăng nhập lại.");
      } else {
        toast.error(`Không lưu được: ${message}`);
      }
      setSubmitting(false);
    }

  };

  if (loading || !user || user.onboarded) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
        Đang tải…
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <Confetti trigger={confetti} />
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
        className="w-full max-w-[520px]"
      >
        <ProgressBar step={step} />

        <div className="p-1.5 rounded-[1.5rem] bg-black/[0.02] dark:bg-white/5 ring-1 ring-black/[0.04] dark:ring-white/10 shadow-[0_2px_12px_rgb(0,0,0,0.04)]">
          <div className="rounded-[calc(1.5rem-6px)] bg-white dark:bg-slate-900 p-7 md:p-9">

        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.25 }}
            >
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                Chào mừng đến Ezyclass
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                Chọn avatar và cho chúng tôi biết bạn muốn được gọi là gì.
              </p>

              <div className="mt-8 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Tên hiển thị *</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={placeholderName}
                    maxLength={80}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Chọn avatar</Label>
                  <AvatarPicker
                    value={avatarKey}
                    onChange={(k) => {
                      setAvatarKey(k);
                      setCustomAvatarFile(null);
                      setCustomAvatarPreview(null);
                    }}
                    customUrl={customAvatarPreview}
                    onCustomFile={handleCustomFile}
                  />
                </div>

                <Button
                  type="button"
                  disabled={!canNext}
                  className="w-full h-11 font-medium"
                  onClick={() => setStep(2)}
                >
                  Tiếp theo <ArrowRight className="size-4" />
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.25 }}
            >
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                Thông tin bổ sung
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                Một vài chi tiết để cá nhân hoá trải nghiệm của bạn.
              </p>

              <div className="mt-8 space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Số điện thoại</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="0901 234 567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dob">Ngày sinh</Label>
                    <Input
                      id="dob"
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                    />
                  </div>
                </div>

                {role === "admin" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="centerName">Tên trung tâm *</Label>
                      <Input
                        id="centerName"
                        value={centerName}
                        onChange={(e) => setCenterName(e.target.value)}
                        placeholder="VD: Trung tâm Anh ngữ Sunrise"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="centerAddress">Địa chỉ</Label>
                      <Input
                        id="centerAddress"
                        value={centerAddress}
                        onChange={(e) => setCenterAddress(e.target.value)}
                        placeholder="VD: 123 Lê Lợi, Q1, TP.HCM"
                      />
                    </div>
                  </>
                )}

                {role === "teacher" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="classroomName">Tên gọi trong lớp *</Label>
                      <Input
                        id="classroomName"
                        value={classroomName}
                        onChange={(e) => setClassroomName(e.target.value)}
                        placeholder="VD: Thầy Minh / Cô Lan"
                        required
                      />
                      <p className="text-xs text-muted-foreground">Học sinh sẽ thấy tên này</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Môn giảng dạy</Label>
                      <SubjectChips value={teachingSubjects} onChange={setTeachingSubjects} />
                    </div>
                  </>
                )}

                {role === "student" && (
                  <>
                    <div className="space-y-2">
                      <Label>Môn học yêu thích <span className="text-xs text-muted-foreground">(tối đa 3)</span></Label>
                      <SubjectChips value={favSubjects} onChange={setFavSubjects} max={3} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="schoolName">Trường đang học</Label>
                      <Input
                        id="schoolName"
                        value={schoolName}
                        onChange={(e) => setSchoolName(e.target.value)}
                        placeholder="VD: THPT Lê Quý Đôn"
                      />
                    </div>
                  </>
                )}

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-11"
                    onClick={() => setStep(1)}
                    disabled={submitting}
                  >
                    <ArrowLeft className="size-4" /> Quay lại
                  </Button>
                  <Button
                    type="button"
                    disabled={submitting || !canSubmit}
                    className="flex-1 h-11 font-medium"
                    onClick={handleSubmit}
                  >
                    {submitting ? "Đang lưu..." : <>Hoàn tất <Check className="size-4" /></>}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
